/*
 * Schedule arithmetic, mirroring backend/utils/scheduleTime.js.
 *
 * A shift can cross midnight — 22:00 to 06:00 is an ordinary night shift here.
 * Everything works in minutes-from-midnight, treating a value at or before the
 * start as "the next day", which makes the wrapped and unwrapped cases one path.
 *
 * The two numbers on the card are not the same thing and the difference is the
 * point of the page:
 *
 *   shift span    end minus start, breaks included
 *   net working   the span minus UNPAID breaks only
 *
 * A paid break is still working time as far as the target is concerned; an
 * unpaid one is not. Subtracting all breaks understates the day and makes
 * everyone look behind.
 */

const DAY = 24 * 60;

/*
 * Sunday first, because that is where this organization's week starts — see
 * WORK_WEEK. The order is display only; the API takes the day names in any
 * order.
 */
export const DAYS = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
];

/*
 * The working week in Bangladesh, where this organization is based. Used as the
 * default selection for a new schedule — defaulting to Monday–Friday would
 * leave Sunday untracked and track Friday, which is backwards here.
 */
export const WORK_WEEK = ["sunday", "monday", "tuesday", "wednesday", "thursday"];

export const DAY_LABEL = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
};

export function toMinutes(value) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? "").trim());
    if (!match) return null;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return null;

    return hours * 60 + minutes;
}

export function toHHmm(minutes) {
    const m = ((Math.round(minutes) % DAY) + DAY) % DAY;
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** Length of start→end, crossing midnight if it must. Equal times give 0. */
export function spanMinutes(start, end) {
    const a = toMinutes(start);
    const b = toMinutes(end);
    if (a === null || b === null) return 0;
    return (b - a + DAY) % DAY;
}

/** How far into the shift a time falls, or null when it is outside. */
export function offsetInShift(shiftStart, shiftEnd, time) {
    const start = toMinutes(shiftStart);
    const at = toMinutes(time);
    if (start === null || at === null) return null;

    const length = spanMinutes(shiftStart, shiftEnd);
    const offset = (at - start + DAY) % DAY;
    return offset <= length ? offset : null;
}

export const isOvernight = (start, end) => {
    const a = toMinutes(start);
    const b = toMinutes(end);
    return a !== null && b !== null && b <= a;
};

/**
 * Everything the card and the editor need about one schedule's day.
 *
 * `breaks` carry their offset so the timeline can place them without redoing
 * the wrap arithmetic — and so a break at 01:00 in a night shift lands after
 * one at 23:00 rather than before it.
 */
export function describeShift(startTime, endTime, breakTimes = []) {
    const span = spanMinutes(startTime, endTime);

    const breaks = breakTimes
        .map((br) => {
            const from = offsetInShift(startTime, endTime, br.startTime);
            const to = offsetInShift(startTime, endTime, br.endTime);
            const length = spanMinutes(br.startTime, br.endTime);

            return {
                ...br,
                from,
                to,
                length,
                // A break the server would refuse: outside the shift, or ending
                // before it starts. Shown rather than hidden.
                invalid: from === null || to === null || to <= from,
            };
        })
        .sort((a, b) => (a.from ?? Infinity) - (b.from ?? Infinity));

    const unpaid = breaks
        .filter((b) => !b.invalid && !b.isPaid)
        .reduce((sum, b) => sum + b.length, 0);
    const paid = breaks.filter((b) => !b.invalid && b.isPaid).reduce((sum, b) => sum + b.length, 0);

    return {
        span,
        breaks,
        paidMinutes: paid,
        unpaidMinutes: unpaid,
        // Unpaid breaks come out; paid ones stay in.
        netMinutes: Math.max(0, span - unpaid),
        overnight: isOvernight(startTime, endTime),
        invalidBreaks: breaks.filter((b) => b.invalid),
    };
}

/**
 * The tracking windows the desktop will actually receive — the same split
 * backend/utils/scheduleTime.js does, so the timeline shows what will happen
 * rather than what was asked for.
 */
export function trackingSlots(startTime, endTime, breakTimes = []) {
    const start = toMinutes(startTime);
    const length = spanMinutes(startTime, endTime);
    if (start === null || length === 0) return [];

    const windows = breakTimes
        .map((br) => [
            offsetInShift(startTime, endTime, br.startTime),
            offsetInShift(startTime, endTime, br.endTime),
        ])
        .filter(([from, to]) => from !== null && to !== null && to > from)
        .sort((a, b) => a[0] - b[0]);

    const slots = [];
    let cursor = 0;
    for (const [from, to] of windows) {
        if (from > cursor) {
            slots.push({ startTime: toHHmm(start + cursor), endTime: toHHmm(start + from), from, to: from });
        }
        cursor = Math.max(cursor, to);
    }
    if (cursor < length) {
        slots.push({ startTime: toHHmm(start + cursor), endTime: toHHmm(start + length), from: cursor, to: length });
    }

    return slots;
}

/** Minutes → "8h 30m", or "30m". */
export function formatSpan(minutes) {
    const total = Math.max(0, Math.round(minutes));
    const h = Math.floor(total / 60);
    const m = total % 60;
    return h ? `${h}h ${m ? `${String(m).padStart(2, "0")}m` : "00m"}` : `${m}m`;
}

/** "22:00" → "10:00 PM" */
export function formatClock(value) {
    const minutes = toMinutes(value);
    if (minutes === null) return "—";

    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

export function scheduleStats(schedules) {
    const active = schedules.filter((s) => s.isActive);

    return {
        total: schedules.length,
        active: active.length,
        individual: schedules.filter((s) => s.scheduleType === "individual").length,
        overnight: schedules.filter((s) => isOvernight(s.startTime, s.endTime)).length,
    };
}
